import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ListOffer,
  ReviewOffer,
} from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  customerGetOffer,
  customerListOffers,
  getOffer,
  listOffers,
} from "services/storeFront";
import { useAppSelector } from "store";
import { useAuthToken } from "hooks/useAuthToken";
import { excludeBuilderOffers } from "utils/builderOffer";
import { getOfferFromProposalId, getOfferToShow } from "utils/offerListHelpers";
import {
  useNavigateToList,
  useNavigateToReview,
} from "utils/useNavigateHelper";

type UseReviewOfferForRoutingResult = {
  offer: ReviewOffer | undefined;
  offerCreatedAt: string | undefined;
  isLoading: boolean;
  error: Error | undefined;
};

const useReviewOfferForRouting = (): UseReviewOfferForRoutingResult => {
  const navigateToList = useNavigateToList();
  const navigateToReview = useNavigateToReview();
  const {
    prospectId,
    offerId: offerIdParam,
    proposalId: proposalIdParam,
  } = useParams();

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const identity = useAppSelector((state) => state.auth.identity);

  const [defaultOfferId, setDefaultOfferId] = useState<string>();
  const [loadedOffer, setLoadedOffer] = useState<{
    offerId: string;
    offer?: ReviewOffer;
  }>();
  const [visibleOffers, setVisibleOffers] = useState<ListOffer[]>();
  const [isOfferLoading, setIsOfferLoading] = useState(false);
  const [isOfferListLoading, setIsOfferListLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const offerId = useMemo(
    () => offerIdParam ?? defaultOfferId,
    [offerIdParam, defaultOfferId]
  );

  const offerCreatedAt = useMemo(() => {
    if (!offerId || !visibleOffers) {
      return undefined;
    }

    return visibleOffers.find((entry) => entry.id === offerId)?.createdAt;
  }, [offerId, visibleOffers]);

  useEffect(() => {
    if (((!cognitoToken || !identity) && !proposalToken) || !offerId) {
      return;
    }

    setIsOfferLoading(true);
    setError(undefined);
    let request;
    if (cognitoToken && identity) {
      request = getOffer(cognitoToken, offerId, identity);
    } else if (proposalToken) {
      request = customerGetOffer(proposalToken, offerId);
    } else {
      setIsOfferLoading(false);
      setError(new Error("Invalid token type."));
      return;
    }

    request
      .then((data) => {
        setLoadedOffer({ offerId, offer: data ?? undefined });
      })
      .catch((fetchError) => {
        setError(
          new Error("There was a problem fetching the offer data.", {
            cause: fetchError,
          })
        );
      })
      .finally(() => {
        setIsOfferLoading(false);
      });
  }, [cognitoToken, proposalToken, offerId, identity]);

  useEffect(() => {
    if (((!cognitoToken || !identity) && !proposalToken) || !prospectId) {
      setIsOfferListLoading(false);
      setVisibleOffers(undefined);
      return;
    }

    setIsOfferListLoading(true);
    let request: Promise<ListOffer[] | undefined>;
    if (cognitoToken && identity) {
      request = listOffers(cognitoToken, prospectId, identity);
    } else if (proposalToken) {
      request = customerListOffers(proposalToken);
    } else {
      setIsOfferListLoading(false);
      setError(new Error("Invalid token type."));
      return;
    }

    request
      ?.then((data) => {
        if (data && data.length > 0) {
          const nextVisibleOffers = isCustomer
            ? excludeBuilderOffers(data)
            : data;

          if (!nextVisibleOffers.length) {
            navigateToList();
            return;
          }

          setVisibleOffers(nextVisibleOffers);

          if (!offerIdParam) {
            let offerToShow;
            if (proposalIdParam) {
              offerToShow =
                getOfferFromProposalId(nextVisibleOffers, proposalIdParam) ??
                getOfferToShow(nextVisibleOffers);
            } else {
              offerToShow = getOfferToShow(nextVisibleOffers);
            }

            setDefaultOfferId(offerToShow.id);
            navigateToReview(offerToShow.id);
          }
        } else {
          navigateToList();
        }
      })
      .catch((fetchError) => {
        setError(
          new Error("There was a problem fetching the offer list data.", {
            cause: fetchError,
          })
        );
      })
      .finally(() => {
        setIsOfferListLoading(false);
      });
  }, [
    cognitoToken,
    identity,
    proposalToken,
    prospectId,
    offerIdParam,
    proposalIdParam,
    isCustomer,
    navigateToList,
    navigateToReview,
  ]);

  const hasLoadedCurrentOffer = loadedOffer?.offerId === offerId;
  const offer = hasLoadedCurrentOffer ? loadedOffer?.offer : undefined;
  const isResolvingOfferId = !offerId && Boolean(prospectId);
  const isWaitingForOffer =
    Boolean(offerId) && !hasLoadedCurrentOffer && !error;
  const isLoading =
    isResolvingOfferId ||
    isOfferListLoading ||
    isOfferLoading ||
    isWaitingForOffer;

  return { offer, offerCreatedAt, isLoading, error };
};

export { useReviewOfferForRouting };
